import type { CSSProperties } from "react";
import styled from "styled-components";

import { controlTransition } from "../foundations/controlStyles";
import { AnnotationContourLayer } from "./AnnotationContourLayer";
import { AnnotationPointMarkerGlyph } from "./AnnotationPointMarkerGlyph";
import {
  AnnotationViewport,
  CapturedVideoSurface,
  EmptyRaster,
  Raster,
} from "./AnnotationViewportPrimitives";
import { ANNOTATION_CAPTURED_FRAME_PREVIEW_IMAGE_MIN_HEIGHT } from "./MediaAnnotationStudioConstraint";
import { targetColorCss } from "./targetColor";
import type { AnnotationFrameDraft, AnnotationTarget, FrameTargetAnnotationDraft } from "./types";

const PreviewArea = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: ${ANNOTATION_CAPTURED_FRAME_PREVIEW_IMAGE_MIN_HEIGHT};
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  container-type: size;
`;

const PreviewViewport = styled(AnnotationViewport)`
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceSunken};
  cursor: default;
`;

const PreviewAnnotationLayer = styled.div<{ $dimmed: boolean; $emphasized: boolean }>`
  position: absolute;
  z-index: ${({ $emphasized }) => ($emphasized ? 2 : 1)};
  inset: 0;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.18 : 1)};
  pointer-events: none;
  ${controlTransition("opacity")}
`;

const PreviewMask = styled.div<{ $emphasized: boolean }>`
  position: absolute;
  inset: 0;
  background: var(--c6-annotation-target-color);
  opacity: ${({ $emphasized }) => ($emphasized ? 0.28 : 0.18)};
  pointer-events: none;
  ${controlTransition("opacity")}
`;

const PreviewPointMarker = styled.span<{ $emphasized: boolean }>`
  position: absolute;
  width: 0.65rem;
  height: 0.65rem;
  color: var(--c6-annotation-target-color);
  filter: ${({ $emphasized }) => ($emphasized ? "drop-shadow(0 0 0.16rem currentColor)" : "none")};
  transform: translate(-50%, -50%);
  transform-origin: center;
  pointer-events: none;
  ${controlTransition("filter")}
`;

export function CapturedFramePreview({
  frame,
  targets,
  mediaSrc,
  poster,
  crossOrigin,
  visibleTargetIds,
  emphasizedTargetIds,
  label,
}: {
  frame: AnnotationFrameDraft;
  targets: readonly { target: AnnotationTarget; draft?: FrameTargetAnnotationDraft }[];
  mediaSrc?: string;
  poster?: string | null;
  crossOrigin?: "anonymous" | "use-credentials";
  visibleTargetIds: ReadonlySet<string>;
  emphasizedTargetIds: ReadonlySet<string>;
  label: string;
}) {
  const emphasisActive = emphasizedTargetIds.size > 0;

  return (
    <PreviewArea data-captured-frame-preview-area="">
      <PreviewViewport
        role="img"
        aria-label={label}
        style={
          {
            "--c6-annotation-aspect-ratio": String(frame.width / frame.height),
          } as CSSProperties
        }
      >
        <Raster>
          {mediaSrc ? (
            <CapturedVideoSurface
              src={mediaSrc}
              poster={poster}
              crossOrigin={crossOrigin}
              controls={false}
              showLabel={false}
              playing={false}
              currentTimeSeconds={frame.timestampSeconds}
              aria-label=""
            />
          ) : poster ? (
            <img src={poster} alt="" />
          ) : (
            <EmptyRaster />
          )}
          {targets.map(({ target, draft }) => {
            if (!visibleTargetIds.has(target.id)) return null;
            const color = targetColorCss(target.color);
            const emphasized = emphasizedTargetIds.has(target.id);
            const dimmed = emphasisActive && !emphasized;
            return (
              <PreviewAnnotationLayer
                key={target.id}
                $dimmed={dimmed}
                $emphasized={emphasized}
                data-annotation-preview-emphasis={
                  emphasized ? "emphasized" : dimmed ? "dimmed" : "idle"
                }
                data-annotation-preview-target={target.id}
                style={{ "--c6-annotation-target-color": color } as CSSProperties}
              >
                {draft?.contours ? (
                  <AnnotationContourLayer
                    contours={draft.contours}
                    presentation="annotation"
                    data-annotation-preview-contours=""
                  />
                ) : draft?.maskSource ? (
                  <PreviewMask $emphasized={emphasized} data-annotation-preview-mask="" />
                ) : null}
                {draft?.points.map((point) => (
                  <PreviewPointMarker
                    key={point.id}
                    $emphasized={emphasized}
                    aria-hidden="true"
                    data-annotation-preview-point=""
                    style={
                      {
                        left: `${point.x * 100}%`,
                        top: `${point.y * 100}%`,
                      } as CSSProperties
                    }
                  >
                    <AnnotationPointMarkerGlyph kind={point.kind} variant="preview" />
                  </PreviewPointMarker>
                ))}
              </PreviewAnnotationLayer>
            );
          })}
        </Raster>
      </PreviewViewport>
    </PreviewArea>
  );
}
